<template>
  <div class="view-box fill-height">
    <keep-alive>
      <banner
        v-if="isATweet"
        color-scheme="gray"
        :banner-message="tweetBannerText"
        :has-close-button="hasSelectedTweetId"
        @close="removeTweetSelection"
      />
    </keep-alive>

    <ul class="conversation-panel">
      <div v-if="hasNoPermissionMessages" class="conversation-locked-overlay">
        <div class="locked-box">
          <div class="lock-icon">🔒</div>
          <h2>{{ $t('CONVERSATION.LOCKED_CONVERSATION_TITLE') }}</h2>
          <p>{{ $t('CONVERSATION.LOCKED_CONVERSATION_DESCRIPTION') }}</p>
        </div>
      </div>
      <transition name="slide-up">
        <li class="spinner--container">
          <span v-if="shouldShowSpinner" class="spinner message" />
        </li>
      </transition>
      <message
        v-for="message in filteredReadMessages"
        :key="message.id"
        class="message--read ph-no-capture"
        data-clarity-mask="True"
        :data="message"
        :is-a-tweet="isATweet"
        :is-a-whatsapp-channel="isAWhatsAppChannel"
        :has-instagram-story="hasInstagramStory"
        :is-web-widget-inbox="isAWebWidgetInbox"
        :reply-data="getReplyMessage(message)"
      />
      <li
        v-show="unreadMessageCount != 0"
        :class="['unread--toast', { 'unread--floating': !autoScroll }]"
      >
        <span class="text-uppercase">
          {{ unreadMessageCount }}
          {{
            unreadMessageCount > 1
              ? $t('CONVERSATION.UNREAD_MESSAGES')
              : $t('CONVERSATION.UNREAD_MESSAGE')
          }}
        </span>
      </li>

      <message
        v-for="message in filteredUnReadMessages"
        :key="message.id"
        class="message--unread ph-no-capture"
        data-clarity-mask="True"
        :data="message"
        :is-a-tweet="isATweet"
        :is-a-whatsapp-channel="isAWhatsAppChannel"
        :has-instagram-story="hasInstagramStory"
        :is-web-widget-inbox="isAWebWidgetInbox"
        :reply-data="getReplyMessage(message)"
      />
    </ul>

    <banner
      class="responsiveBanner"
      v-if="showBannerCanReply"
      color-scheme="alert"
      :banner-message="replyWindowBannerMessage"
    />

    <banner
      class="responsiveBanner"
      v-if="showQueueBanner"
      :color-scheme="showBannerCanReply ? 'secondary' : 'alert'"
      :banner-message="queueBannerMessage"
      has-close-button
      @close="hideQueueBanner"
    />

    <div
      v-if="!autoScroll && unreadMessageCount > 0"
      class="unread--fixed-toast"
    >
      <span class="text-uppercase">
        {{ unreadMessageCount }}
        {{
          unreadMessageCount > 1
            ? $t('CONVERSATION.UNREAD_MESSAGES')
            : $t('CONVERSATION.UNREAD_MESSAGE')
        }}
      </span>
    </div>
    <div
      class="conversation-footer"
      :class="{ 'modal-mask': isPopoutReplyBox }"
    >
      <div v-if="isAnyoneTyping" class="typing-indicator-wrap">
        <div class="typing-indicator">
          {{ typingUserNames }}
          <img
            class="gif"
            src="~dashboard/assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>

      <keep-alive>
        <reply-box
          v-if="showReplyBox"
          :conversation-id="currentChat.id"
          :is-a-tweet="isATweet"
          :selected-tweet="selectedTweet"
          :popout-reply-box.sync="isPopoutReplyBox"
          :reply-message.sync="messageToReply"
          :allow-private-note-only="allowPrivateNoteOnly"
          @click="showPopoutReplyBox"
        />
      </keep-alive>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ReplyBox from './ReplyBox';
import Message from './Message';
import conversationMixin, {
  filterDuplicateSourceMessages,
} from '../../../mixins/conversations';
import Banner from 'dashboard/components/ui/Banner.vue';
import { getTypingUsersText } from '../../../helper/commons';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import inboxMixin from 'shared/mixins/inboxMixin';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { isEscape } from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';

export default {
  components: {
    Message,
    ReplyBox,
    Banner,
  },
  mixins: [conversationMixin, inboxMixin, eventListenerMixins],
  props: {
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    autoScroll: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      selectedTweetId: null,
      isPopoutReplyBox: false,
      messageToReply: null,
      showQueueBannerOnce: true,
      refreshInterval: null,
      isRefreshing: false,
      hasNoPermissionMessages: false,
    };
  },
  computed: {
    ...mapGetters({
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
      allConversations: 'getAllConversations',
      inboxesList: 'inboxes/getInboxes',
      listLoadingStatus: 'getAllMessagesLoaded',
      loadingChatList: 'getChatListLoadingStatus',
      isObserverMode: 'observerMode/isObserverMode',
      isEmbedding: 'isEmbedding',
      conversationStats: 'conversationStats/getStats',
    }),
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    hasSelectedTweetId() {
      return !!this.selectedTweetId;
    },
    tweetBannerText() {
      return !this.selectedTweetId
        ? this.$t('CONVERSATION.SELECT_A_TWEET_TO_REPLY')
        : `
          ${this.$t('CONVERSATION.REPLYING_TO')}
          ${this.selectedTweet.content}` || '';
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    teamMembers() {
      const members =
        this.$store.getters['teamMembers/getTeamMembers'](this.teamId) || [];
      return members.filter(member => member.role !== 'team_supervisor');
    },
    isAdminOrSuperAdmin() {
      return (
        this.currentRole === 'SuperAdmin' ||
        this.currentRole === 'administrator'
      );
    },
    isUserInTeam() {
      const teamMembers = this.currentChat.meta?.team?.members || [];
      return teamMembers.includes(this.currentUser.id);
    },
    typingUserNames() {
      const userList = this.typingUsersList;

      if (this.isAnyoneTyping) {
        const userListAsName = getTypingUsersText(userList);
        return userListAsName;
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    getReadMessages() {
      return this.readMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    getUnReadMessages() {
      return this.unReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    isATweet() {
      return this.conversationType === 'tweet';
    },
    hasInstagramStory() {
      return this.conversationType === 'instagram_direct_message';
    },
    selectedTweet() {
      if (this.selectedTweetId) {
        const { messages = [] } = this.currentChat;
        const [selectedMessage] = messages.filter(
          message => message.id === this.selectedTweetId
        );
        return selectedMessage || {};
      }
      return '';
    },
    isRightOrLeftIcon() {
      if (this.isContactPanelOpen) {
        return 'arrow-chevron-right';
      }
      return 'arrow-chevron-left';
    },
    getLastSeenAt() {
      const { contact_last_seen_at: contactLastSeenAt } = this.currentChat;
      return contactLastSeenAt;
    },
    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel && !this.currentChat.can_reply) {
        return this.$t('CONVERSATION.WINDOW_24H_BANNER_MESSAGE');
      }
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: attrs = {} } = this.inbox;
        return attrs.agent_reply_time_window_message || '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      return this.isAWhatsAppChannel ? REPLY_POLICY.FACEBOOK : '';
    },
    replyWindowLinkText() {
      return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
    },
    unreadMessageCount() {
      return this.currentChat.unread_count;
    },
    showReplyBox() {
      if (this.currentRole == 'embed' || this.isEmbedding) {
        return false;
      }

      return true;
    },
    allowPrivateNoteOnly() {
      return (
        this.inbox?.lock_to_single_conversation === false &&
        this.currentChat?.status === 'resolved'
      );
    },
    showBannerCanReply() {
      if (this.currentRole === 'embed' || this.isEmbedding) return false;
      return !this.currentChat.can_reply;
    },
    queueCount() {
      return this.conversationStats?.unAssignedCount || 0;
    },
    queueBannerMessage() {
      return this.$t('CONVERSATION.QUEUE_ALERT', { count: this.queueCount });
    },
    showQueueBanner() {
      const hasAgentAssigned =
        this.currentChat?.meta?.assignee ||
        this.currentChat?.assignee ||
        this.currentChat?.assignee_id;

      return (
        hasAgentAssigned && this.queueCount > 0 && this.showQueueBannerOnce
      );
    },
    filteredReadMessages() {
      if (this.hasNoPermissionMessages) return [];
      return this.getReadMessages;
    },
    filteredUnReadMessages() {
      if (this.hasNoPermissionMessages) return [];
      return this.getUnReadMessages;
    },
  },
  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) return;

      this.fetchAllAttachmentsFromCurrentChat();
      this.selectedTweetId = null;
      this.showQueueBannerOnce = true;

      this.stopAutoRefresh();
      this.startAutoRefresh();
      this.checkPermissions();
    },
    'currentChat.permissions': function (newPermissions, oldPermissions) {
      if (newPermissions !== oldPermissions) {
        this.checkPermissions();
      }
    },
    'currentChat.id': function (newChatId, oldChatId) {
      this.checkPermissions();
    },
    'currentChat.messages': function (newMessages) {
      this.checkPermissions();
    },
    'currentChat.permissions': function () {
      this.checkPermissions();
    },
    'currentChat.isAdmin': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateMessages();
      }
    },
    'currentChat.meta.team': function (newTeam, oldTeam) {
      this.checkPermissions();
    },
  },
  created() {
    bus.$on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    bus.$on(BUS_EVENTS.SET_TWEET_REPLY, this.setSelectedTweet);
    bus.$on(BUS_EVENTS.SHOW_LOG_CONVERSATION, this.fetchSyncMessages);
  },
  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    window.bus.$on('reply-to-message', this.setReplyMessage);

    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.removeBusListeners();
    this.removeScrollListener();
    window.bus.$off('reply-to-message', this.setReplyMessage);

    this.stopAutoRefresh();
  },
  methods: {
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    checkPermissions() {
      // 🔥 Sempre concede permissão
      this.hasNoPermissionMessages = false;
      return;

      const messages = this.currentChat.messages || [];
      const lastMessage = messages[messages.length - 1];
      const isAdmin = this.isAdminOrSuperAdmin;
      const isSupervisorOrTeamSupervisor = [
        'supervisor',
        'team_supervisor',
      ].includes(this.currentRole);
      const isPrivateTeam = this.currentChat.meta?.team?.private === true;
      const isUserInTeam = this.isUserInTeam;
      const assigneeId = this.currentChat.meta?.assignee?.id;
      const userId = this.currentUser.id;
      const isAssigneeValid = assigneeId === userId;

      if (isAdmin || isSupervisorOrTeamSupervisor) {
        this.hasNoPermissionMessages = false;
      } else if (
        !isAdmin &&
        !isSupervisorOrTeamSupervisor &&
        !isAssigneeValid &&
        isPrivateTeam &&
        !isUserInTeam
      ) {
        this.hasNoPermissionMessages = true;
      } else if (
        !isAdmin &&
        !isSupervisorOrTeamSupervisor &&
        !isAssigneeValid &&
        !isPrivateTeam
      ) {
        this.hasNoPermissionMessages = false;
      } else {
        this.hasNoPermissionMessages = false;
      }
    },
    updateMessages() {
      if (this.currentChat.isAdmin) {
        this.$nextTick(() => {
          this.hasNoPermissionMessages = false;
          this.$forceUpdate();
        });
      }
    },
    removeBusListeners() {
      bus.$off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
      bus.$off(BUS_EVENTS.SET_TWEET_REPLY, this.setSelectedTweet);
      bus.$off(BUS_EVENTS.SHOW_LOG_CONVERSATION, this.fetchSyncMessages);
    },
    setSelectedTweet(tweetId) {
      this.selectedTweetId = tweetId;
    },
    onScrollToMessage({ messageId = '' } = {}) {
      if (!this.autoScroll) return;

      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth' });
          this.fetchPreviousMessages();
        } else {
          this.scrollToBottom();
        }
      });

      if (!this.isObserverMode) {
        this.makeMessagesRead();
      }
    },
    showPopoutReplyBox() {
      this.isPopoutReplyBox = !this.isPopoutReplyBox;
    },
    closePopoutReplyBox() {
      this.isPopoutReplyBox = false;
    },
    handleKeyEvents(e) {
      if (isEscape(e)) {
        this.closePopoutReplyBox();
      }
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => {
        if (this.autoScroll) {
          this.scrollToBottom();
        }
      });
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      let relevantMessages = [];
      if (this.unreadMessageCount > 0) {
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else {
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }
      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    onToggleContactPanel() {
      this.$emit('contact-panel-toggle');
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },
    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },
    handleScroll(e) {
      bus.$emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },
    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    removeTweetSelection() {
      this.selectedTweetId = null;
    },
    getReplyMessage(message) {
      if (message.content_attributes) {
        let messages = [...this.getReadMessages, ...this.getUnReadMessages];
        return messages.find(
          m => m.id === message.content_attributes.in_reply_to
        );
      }
      return null;
    },
    fetchSyncMessages() {
      window.location.reload();
    },
    setReplyMessage(messageData) {
      this.messageToReply = messageData;
    },
    hideQueueBanner() {
      this.showQueueBannerOnce = false;
    },
    async refreshMessages() {
      if (!this.currentChat?.id || this.isRefreshing) return;

      this.isRefreshing = true;

      try {
        await this.$store.dispatch('getConversation', this.currentChat.id);
      } catch (e) {
        console.error('Error while refreshing conversation:', e);
      } finally {
        this.isRefreshing = false;
      }
    },
    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        if (!this.isAnyoneTyping) {
          this.refreshMessages();
        }
      }, 5 * 60 * 1000);
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.spinner--container {
  min-height: var(--space-jumbo);
}

.view-box.fill-height {
  height: auto;
  flex-grow: 1;
  min-width: 0;
}

.modal-mask {
  &::v-deep {
    .ProseMirror-woot-style {
      max-height: 40rem;
    }

    .reply-box {
      border: 1px solid var(--color-border);
      max-width: 120rem;
      width: 70%;
    }

    .reply-box .reply-box__top {
      position: relative;
      min-height: 44rem;
    }

    .reply-box__top .input {
      min-height: 44rem;
    }

    .emoji-dialog {
      position: fixed;
      left: unset;
      position: absolute;
      bottom: var(--space-smaller);
    }
  }
}

.unread--fixed-toast {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-background-light);
  border-radius: var(--border-radius-medium);
  padding: var(--space-smaller) var(--space-normal);
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  white-space: nowrap;
}

.view-box {
  position: relative;
}

.locked-box {
  text-align: center;
  max-width: 400px;
}

.lock-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.conversation-panel {
  position: relative;
}

.conversation-locked-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.responsiveBanner {
  @media screen and (max-width: 768px) {
    display: none;
  }
}
</style>
